// plugins/source-tracker.client.ts
import type { RouteLocationNormalized } from 'vue-router';

declare global {
  interface Window {
    __trackingContext?: {
      source_page: string;
      current_page: string;
    };
    gtag?: (...args: any[]) => void;
  }
}

let previousRoutePath: string | null = null;

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  const route = useRoute();
  const { gtag } = useGtag();

  // Initialize tracking context immediately on page load (refresh/direct access)
  if (process.client) {
    const initialTracking = getTrackingData(route);
    window.__trackingContext = {
      source_page: 'direct',
      current_page: initialTracking.pageType
        ? `${initialTracking.pageType} - ${initialTracking.identifier}`
        : route.fullPath,
    };

    // 🔹 Trigger initial gtag event
    // gtag('event', 'page_view_init', {
    //   source_page: window.__trackingContext.source_page,
    //   current_page: window.__trackingContext.current_page,
    // });
  }

  router.beforeEach((to, from) => {
    previousRoutePath = from.fullPath || null;

    const sourceTracking = getTrackingData(from);
    const destinationTracking = getTrackingData(to);

    if (process.client) {
      window.__trackingContext = {
        source_page: sourceTracking.pageType
          ? `${sourceTracking.pageType} - ${sourceTracking.identifier}`
          : from.fullPath,
        current_page: destinationTracking.pageType
          ? `${destinationTracking.pageType} - ${destinationTracking.identifier}`
          : to.fullPath,
      };

      //  gtag('event', 'page_track_event', {
      //     source_page: window.__trackingContext.source_page,
      //     current_page: window.__trackingContext.current_page,
      //   });
    }
  });

  nuxtApp.provide('getSourcePage', () => previousRoutePath);
});

function getTrackingData(route: RouteLocationNormalized) {
  const path = route.path.toLowerCase();
  const query = route.query;

  if ((path === '/categories' || path === '/categories/v2') && query.parent) {
    return { pageType: 'clp', identifier: query.parent };
  }
  if (
    (path === '/categories/search' ||
      path === '/categories/search-v2' ||
      path === '/filter/v2') &&
    (query.q || query.sub_category)
  ) {
    return { pageType: 'sub_clp', identifier: query.q || query.sub_category };
  }
  if (path.startsWith('/products/')) {
    const segments = path.split('/');
    return { pageType: 'pdp', identifier: segments[2]?.toUpperCase() ?? 'unknown' };
  }
  if (path.startsWith('/providers/')) {
    const segments = path.split('/');
    return { pageType: 'plp', identifier: segments[2]?.toUpperCase() ?? 'unknown' };
  }
  if (path === '/account/orders') return { pageType: 'My orders', identifier: 'orders' };
  if (path === '/categories/v2') return { pageType: 'clp', identifier: 'v2' };
  if (path === '/cart') return { pageType: 'cart', identifier: 'cart' };
  if (path === '/deals') return { pageType: 'deals', identifier: 'deals' };
  if (path === '/menu') return { pageType: 'profile', identifier: 'menu' };
  if (path === '/account/profile') return { pageType: 'profile', identifier: path };
  if (path === '/account/addresses') return { pageType: 'addresses', identifier: path };
  if (path === '/') return { pageType: 'Homepage', identifier: 'Homepage' };
  if (path === '/select-location') return { pageType: 'Select Location', identifier: 'Select Location' };
  if (path === '/account/tickets') return { pageType: 'My Tickets', identifier: 'My Tickets' };
  if (path === '/support/faq') return { pageType: 'Support(faq)', identifier: 'Support(faq)' };
  if (path === '/support/raise_a_request') return { pageType: 'Raise request page', identifier: 'Raise request page' };
  if (path === '/about/aboutUs') return { pageType: 'About us', identifier: 'About us' };
  if (path === '/about/terms') return { pageType: 'T&C', identifier: 'T&C' };
  if (path === '/about/privacy-policy') return { pageType: 'Privacy-policy', identifier: 'Privacy-policy' };
  if (path === '/account/rating') {
    return { pageType: 'Rating', identifier: path };
  }
  if (path === '/collections/giftcards') {
    return { pageType: 'PLP', identifier: path };
  }
  if (path === '/collections/gift-cards/cart') {
    return { pageType: 'Cart', identifier: path };
  }
  if (path.startsWith('/collections/gift-cards/order-summary/')) {
    return { pageType: 'Order Summary', identifier: path };
  }
  if (path.startsWith('/collections/gift-cards/details/')) {
    return { pageType: 'GiftcardDetail', identifier: path };
  }
  if (path.startsWith('/collections/gift-cards/')) {
    return { pageType: 'PDP', identifier: path };
  }
  
  const segments = path.split('/').filter(Boolean);
  return {
    pageType: segments[0] || 'direct',
    identifier: segments[1] || segments[0] || 'home',
  };
}
